import { create } from "zustand";
import {
  createInitialGame,
  launchIncubator,
  placeBuilding,
  sellBuilding,
  setBuildingLane,
  setBuildingRecipe,
  setLaunchMode,
  setPriority,
  tickGame,
  toggleBuildingPaused,
  type BuildingType,
  type GameEvent,
  type GameState,
  type Lane,
  type LaunchMode,
  type Priority,
  type UnitType,
} from "../game";
import { audio } from "../audio/audio";

export interface ToastMessage {
  id: string;
  title: string;
  text: string;
  enemy?: boolean;
  expiresAt: number;
}

export interface VisualEvent extends GameEvent {
  expiresAt: number;
}

interface GameStore {
  game: GameState;
  started: boolean;
  paused: boolean;
  buildTool: BuildingType | null;
  hoveredCell: { gx: number; gz: number } | null;
  selectedId: string | null;
  supplyLens: boolean;
  helpOpen: boolean;
  muted: boolean;
  toasts: ToastMessage[];
  effects: VisualEvent[];
  advance: (dt: number) => void;
  start: () => void;
  restart: () => void;
  togglePause: () => void;
  chooseBuildTool: (type: BuildingType | null) => void;
  hoverCell: (cell: { gx: number; gz: number } | null) => void;
  placeAt: (gx: number, gz: number) => void;
  selectEntity: (id: string | null) => void;
  sellSelected: () => void;
  setRecipe: (type: UnitType) => void;
  setLane: (lane: Lane) => void;
  setMode: (mode: LaunchMode) => void;
  setSupplyPriority: (priority: Priority) => void;
  toggleSelectedPaused: () => void;
  launchSelected: () => void;
  toggleSupplyLens: () => void;
  setHelpOpen: (open: boolean) => void;
  toggleMuted: () => void;
  notify: (title: string, text: string, enemy?: boolean) => void;
}

let toastId = 0;

function eventCue(event: GameEvent) {
  const enemy = event.owner === "ai";
  switch (event.type) {
    case "build": audio.play("place", enemy); break;
    case "cargo": audio.play("cargo", enemy); break;
    case "production": audio.play("produce", enemy); break;
    case "spawn": audio.play("spawn", enemy); break;
    case "attack": audio.play("shot", enemy); break;
    case "hit": audio.play("impact", enemy); break;
    case "destroy": audio.play("destroy", enemy); break;
    case "win": audio.play(event.owner === "player" ? "victory" : "defeat", enemy); break;
  }
}

function eventToast(event: GameEvent): Omit<ToastMessage, "id" | "expiresAt"> | null {
  if (event.type === "spawn" && event.unitType === "ramhorn") {
    return { title: event.owner === "player" ? "Siege brood launched" : "Enemy siege detected", text: "Ramhorn entering the amberline.", enemy: event.owner === "ai" };
  }
  if (event.type === "destroy" && event.buildingType && event.buildingType !== "veinway") {
    return { title: event.owner === "player" ? "Structure lost" : "Enemy structure broken", text: event.message ?? "The supply graph has changed.", enemy: event.owner === "player" };
  }
  if (event.type === "win") {
    return { title: event.owner === "player" ? "Heartforge ruptured" : "Your Heartforge fell", text: event.owner === "player" ? "The rival genome is collapsing." : "Rebuild the line and answer harder.", enemy: event.owner === "ai" };
  }
  return null;
}

export const useGameStore = create<GameStore>((set, get) => ({
  game: createInitialGame(20260710),
  started: false,
  paused: false,
  buildTool: null,
  hoveredCell: null,
  selectedId: null,
  supplyLens: false,
  helpOpen: false,
  muted: false,
  toasts: [],
  effects: [],

  advance: (dt) => {
    const state = get();
    if (!state.started || state.paused || state.helpOpen || state.game.phase !== "playing") return;
    const previousTick = state.game.tick;
    const events = tickGame(state.game, Math.min(dt, 0.2));
    events.forEach(eventCue);
    const additions = events.map(eventToast).filter((item): item is NonNullable<typeof item> => Boolean(item));
    if (state.game.tick !== previousTick || additions.length) {
      const now = performance.now();
      const fresh = additions.map((item) => ({ ...item, id: `toast-${++toastId}`, expiresAt: now + 4200 }));
      const freshEffects = events
        .filter((event) => event.type === "attack" || event.type === "hit" || event.type === "destroy" || event.type === "spawn")
        .map((event) => ({ ...event, expiresAt: now + (event.type === "destroy" ? 1100 : 480) }));
      audio.setIntensity(Math.min(1, 0.22 + state.game.units.length * 0.025 + state.game.time / 900));
      set({
        game: { ...state.game },
        toasts: [...state.toasts.filter((toast) => toast.expiresAt > now), ...fresh].slice(-4),
        effects: [...state.effects.filter((effect) => effect.expiresAt > now), ...freshEffects].slice(-48),
      });
    }
  },

  start: () => {
    audio.unlock();
    set({ started: true, paused: false, helpOpen: false });
    get().notify("Heartforge online", "Build Veinways to the two nearby deposits.");
  },

  restart: () => {
    audio.unlock();
    set({
      game: createInitialGame(Math.floor(performance.now()) || 1),
      started: true,
      paused: false,
      buildTool: null,
      hoveredCell: null,
      selectedId: null,
      helpOpen: false,
      toasts: [],
      effects: [],
    });
    get().notify("Fresh genome", "Feed the line. Break the rival Heartforge.");
  },

  togglePause: () => set((state) => ({ paused: !state.paused })),
  chooseBuildTool: (type) => set({ buildTool: type, selectedId: null }),
  hoverCell: (cell) => set({ hoveredCell: cell }),

  placeAt: (gx, gz) => {
    const { game, buildTool } = get();
    if (!buildTool) return;
    const result = placeBuilding(game, "player", buildTool, gx, gz);
    if (!result.ok) {
      audio.play("deny");
      get().notify("Placement blocked", result.reason ?? "That cell cannot accept this structure.");
      return;
    }
    result.events.forEach(eventCue);
    set({ game: { ...game }, selectedId: result.events[0]?.entityId ?? null });
  },

  selectEntity: (id) => set({ selectedId: id, buildTool: null }),

  sellSelected: () => {
    const { game, selectedId } = get();
    if (!selectedId) return;
    const result = sellBuilding(game, "player", selectedId);
    if (!result.ok) return get().notify("Cannot reclaim", result.reason ?? "That structure stays.");
    result.events.forEach(eventCue);
    set({ game: { ...game }, selectedId: null });
  },

  setRecipe: (type) => {
    const { game, selectedId } = get(); if (!selectedId) return;
    const result = setBuildingRecipe(game, "player", selectedId, type);
    if (result.ok) set({ game: { ...game } });
  },
  setLane: (lane) => {
    const { game, selectedId } = get(); if (!selectedId) return;
    const result = setBuildingLane(game, "player", selectedId, lane);
    if (result.ok) set({ game: { ...game } });
  },
  setMode: (mode) => {
    const { game, selectedId } = get(); if (!selectedId) return;
    const result = setLaunchMode(game, "player", selectedId, mode);
    if (result.ok) set({ game: { ...game } });
  },
  setSupplyPriority: (priority) => {
    const { game, selectedId } = get(); if (!selectedId) return;
    const result = setPriority(game, "player", selectedId, priority);
    if (result.ok) set({ game: { ...game } });
  },
  toggleSelectedPaused: () => {
    const { game, selectedId } = get(); if (!selectedId) return;
    const result = toggleBuildingPaused(game, "player", selectedId);
    if (!result.ok) return get().notify("Always active", result.reason ?? "This system cannot pause.");
    set({ game: { ...game } });
  },
  launchSelected: () => {
    const { game, selectedId } = get(); if (!selectedId) return;
    const result = launchIncubator(game, "player", selectedId);
    if (!result.ok) return get().notify("Brood not ready", result.reason ?? "Grow units before launching.");
    result.events.forEach(eventCue);
    set({ game: { ...game } });
  },
  toggleSupplyLens: () => set((state) => ({ supplyLens: !state.supplyLens })),
  setHelpOpen: (open) => set({ helpOpen: open }),
  toggleMuted: () => {
    const muted = !get().muted;
    audio.unlock(); audio.setMuted(muted); set({ muted });
  },
  notify: (title, text, enemy = false) => set((state) => ({
    toasts: [...state.toasts.filter((toast) => toast.expiresAt > performance.now()), {
      id: `toast-${++toastId}`, title, text, enemy, expiresAt: performance.now() + 4200,
    }].slice(-4),
  })),
}));
